import React, { useMemo } from "react";
import { Table, Label, Icon, Button } from "semantic-ui-react";
import { decodeProto, TYPES, typeToString } from "./protobufDecoder";
import {
  decodeFixed32,
  decodeFixed64,
  decodeStringOrBytes,
  decodeVarintParts
} from "./protobufPartDecoder";
import ProtobufDisplay from "./ProtobufDisplay";

// 格式化字节范围显示
function formatByteRange(byteRange) {
  if (!byteRange || byteRange.length < 2) return "N/A";
  const start = byteRange[0];
  const end = byteRange[1];
  const length = end - start;
  
  if (length === 0) return `${start}`;
  if (length === 1) return `${start}`;
  return `${start}-${end-1} (${length} B)`;
}

// 格式化字段号显示
function formatFieldNumber(index) {
  if (index === -1) return "Message Delimiter";
  if (index === 0) return "Unknown";
  return `${index}`;
}

// 获取字段类型颜色
function getTypeColor(type) {
  switch (type) {
    case TYPES.VARINT:
      return 'blue';
    case TYPES.LENDELIM:
      return 'green';
    case TYPES.FIXED32:
      return 'orange';
    case TYPES.FIXED64:
      return 'purple';
    case TYPES.MSG_LEN_DELIMITER:
      return 'red';
    default:
      return 'grey';
  }
}

// 大数据量hex显示组件
function HexDataDisplay({ hexData, byteRange }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [showAsTable, setShowAsTable] = React.useState(false);
  
  const bytes = hexData.split(' ').filter(b => b.trim());
  const totalBytes = bytes.length;
  const previewBytes = bytes.slice(0, 16);
  
  const formatHexTable = (bytes) => {
    const rows = [];
    for (let i = 0; i < bytes.length; i += 16) {
      const row = bytes.slice(i, i + 16);
      const offset = i;
      const hex = row.map(b => b.padStart(2, '0')).join(' ');
      const ascii = row.map(b => {
        const charCode = parseInt(b, 16);
        return (charCode >= 32 && charCode <= 126) ? String.fromCharCode(charCode) : '.';
      }).join('');
      
      rows.push({ offset, hex, ascii });
    }
    return rows;
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <div>
          <Label color="teal" size="small">
            <Icon name="code" />
            {totalBytes} bytes
          </Label>
          <Label color="grey" size="small">
            {formatByteRange(byteRange)}
          </Label>
        </div>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <Button 
            size="mini" 
            compact 
            onClick={() => setShowAsTable(!showAsTable)}
          >
            <Icon name={showAsTable ? 'list' : 'table'} />
            {showAsTable ? 'List' : 'Table'}
          </Button>
          <Button 
            size="mini" 
            compact 
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon name={isExpanded ? 'compress' : 'expand'} />
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </div>
      
      {showAsTable ? (
        <div style={{ 
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #e9ecef',
          overflow: 'auto',
          maxHeight: isExpanded ? 'none' : '300px'
        }}>
          <Table celled compact size="small" style={{ margin: 0, width: '100%' }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ width: '15%', minWidth: '80px' }}>Offset</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '70%', minWidth: '200px' }}>Hex</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '15%', minWidth: '80px' }}>ASCII</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {formatHexTable(isExpanded ? bytes : previewBytes).map((row, i) => (
                <Table.Row key={i}>
                  <Table.Cell style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-word' }}>
                    {row.offset.toString(16).padStart(4, '0').toUpperCase()}
                  </Table.Cell>
                  <Table.Cell style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
                    {row.hex}
                  </Table.Cell>
                  <Table.Cell style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-word' }}>
                    {row.ascii}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : (
        <div style={{ 
          fontFamily: 'monospace', 
          fontSize: '11px',
          backgroundColor: '#f8f9fa',
          padding: '12px',
          borderRadius: '6px',
          border: '1px solid #e9ecef',
          lineHeight: '1.4',
          maxHeight: isExpanded ? 'none' : '200px',
          overflow: isExpanded ? 'visible' : 'auto'
        }}>
          {isExpanded ? hexData : hexData.substring(0, 100) + '...'}
        </div>
      )}
    </div>
  );
}

function ProtobufVarintPart(props) {
  const { value } = props;
  
  // 使用 useMemo 缓存解码结果
  const decoded = useMemo(() => decodeVarintParts(value), [value]);

  return (
    <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
      {decoded.map((d, i) => (
        <div key={i} style={{ marginBottom: '4px' }}>
          <Label size="mini" color="blue">
            {d.type}
          </Label>
          <span style={{ marginLeft: '8px' }}>{d.value}</span>
        </div>
      ))}
    </div>
  );
}

function ProtobufStringOrBytesPart(props) {
  const { value } = props;
  
  if (value.type === "string") {
    return (
      <div style={{ 
        fontFamily: 'monospace', 
        fontSize: '12px',
        backgroundColor: '#e8f5e8',
        padding: '8px',
        borderRadius: '4px',
        border: '1px solid #c3e6c3'
      }}>
        <Label size="mini" color="green">String</Label>
        <div style={{ marginTop: '4px' }}>{value.value}</div>
      </div>
    );
  } else if (value.type === "string|bytes") {
    // 对于string|bytes类型，尝试同时显示字符串和hex
    const hexData = value.value || '';
    const bytes = hexData.split(' ').filter(b => b.trim());
    
    // 如果数据为空，显示空状态
    if (!hexData || bytes.length === 0) {
      return (
        <div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <Label color="blue" size="small">
              <Icon name="code" />
              0 bytes
            </Label>
            <Label color="grey" size="small">
              {formatByteRange(props.byteRange)}
            </Label>
          </div>
          
          <div style={{ 
            backgroundColor: '#f8f9fa',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #e9ecef',
            color: '#6c757d',
            fontStyle: 'italic'
          }}>
            <Label size="mini" color="grey">Empty</Label>
            <div style={{ marginTop: '4px' }}>No data</div>
          </div>
        </div>
      );
    }
    
    // 尝试解码为字符串
    let stringValue = '';
    let isReadableString = false;
    try {
      const byteArray = new Uint8Array(bytes.map(b => parseInt(b, 16)));
      const decoder = new TextDecoder('utf-8', { fatal: false });
      stringValue = decoder.decode(byteArray);
      // 检查是否为可读的字符串（主要是可打印字符）
      isReadableString = stringValue.length > 0 && 
                        stringValue.length <= 200 && 
                        /^[\x20-\x7E]*$/.test(stringValue);
    } catch (e) {
      // 解码失败，保持空字符串
    }
    
    return (
      <div>
        {/* 显示字符串（如果可读） */}
        {isReadableString && (
          <div style={{ 
            backgroundColor: '#e8f5e8',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #c3e6c3',
            marginBottom: '8px'
          }}>
            <Label size="mini" color="green">String</Label>
            <div style={{ marginTop: '4px', fontFamily: 'monospace' }}>{stringValue}</div>
          </div>
        )}
        
        {/* 显示hex数据 */}
        <HexDataDisplay hexData={hexData} byteRange={props.byteRange} />
      </div>
    );
  } else {
    // 对于bytes类型，尝试同时显示字符串和hex
    const hexData = value.value || '';
    const bytes = hexData.split(' ').filter(b => b.trim());
    
    // 如果数据为空，显示空状态
    if (!hexData || bytes.length === 0) {
      return (
        <div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <Label color="blue" size="small">
              <Icon name="code" />
              0 bytes
            </Label>
            <Label color="grey" size="small">
              {formatByteRange(props.byteRange)}
            </Label>
          </div>
          
          <div style={{ 
            backgroundColor: '#f8f9fa',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #e9ecef',
            color: '#6c757d',
            fontStyle: 'italic'
          }}>
            <Label size="mini" color="grey">Empty</Label>
            <div style={{ marginTop: '4px' }}>No data</div>
          </div>
        </div>
      );
    }
    
    // 尝试解码为字符串
    let stringValue = '';
    let isReadableString = false;
    try {
      const byteArray = new Uint8Array(bytes.map(b => parseInt(b, 16)));
      const decoder = new TextDecoder('utf-8', { fatal: false });
      stringValue = decoder.decode(byteArray);
      // 检查是否为可读的字符串（主要是可打印字符）
      isReadableString = stringValue.length > 0 && 
                        stringValue.length <= 200 && 
                        /^[\x20-\x7E]*$/.test(stringValue);
    } catch (e) {
      // 解码失败，保持空字符串
    }
    
    return (
      <div>
        {/* 显示字符串（如果可读） */}
        {isReadableString && (
          <div style={{ 
            backgroundColor: '#e8f5e8',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #c3e6c3',
            marginBottom: '8px'
        }}>
            <Label size="mini" color="green">String</Label>
            <div style={{ marginTop: '4px', fontFamily: 'monospace' }}>{stringValue}</div>
          </div>
        )}
        
        {/* 显示hex数据 */}
        <HexDataDisplay hexData={hexData} byteRange={props.byteRange} />
      </div>
    );
  }
}

function ProtobufFixed64Part(props) {
  const { value } = props;
  
  // 使用 useMemo 缓存解码结果
  const decoded = useMemo(() => decodeFixed64(value), [value]);

  return (
    <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
      {decoded.map((d, i) => (
        <div key={i} style={{ marginBottom: '4px' }}>
          <Label size="mini" color="purple">
            {d.type}
          </Label>
          <span style={{ marginLeft: '8px' }}>{d.value}</span>
        </div>
      ))}
    </div>
  );
}

function ProtobufFixed32Part(props) {
  const { value } = props;
  
  // 使用 useMemo 缓存解码结果
  const decoded = useMemo(() => decodeFixed32(value), [value]);

  return (
    <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
      {decoded.map((d, i) => (
        <div key={i} style={{ marginBottom: '4px' }}>
          <Label size="mini" color="orange">
            {d.type}
          </Label>
          <span style={{ marginLeft: '8px' }}>{d.value}</span>
        </div>
      ))}
    </div>
  );
}

function getProtobufPart(part, originalData) {
  switch (part.type) {
    case TYPES.VARINT:
      return [<ProtobufVarintPart value={part.value} />];
    case TYPES.LENDELIM:
      // TODO: Support repeated field
      let decoded = decodeProto(part.value);
      if (part.value.length > 0 && decoded.leftOver.length === 0) {
        return [<ProtobufDisplay value={decoded} originalData={originalData} />, "protobuf"];
      } else {
        decoded = decodeStringOrBytes(part.value);
        return [<ProtobufStringOrBytesPart value={decoded} byteRange={part.byteRange} />, decoded.type];
      }
    case TYPES.FIXED64:
      return [<ProtobufFixed64Part value={part.value} />];
    case TYPES.FIXED32:
      return [<ProtobufFixed32Part value={part.value} />];
    case TYPES.MSG_LEN_DELIMITER:
      return [
        <div style={{ 
          backgroundColor: '#fff3cd', 
          padding: '8px', 
          borderRadius: '4px',
          border: '1px solid #ffeaa7'
        }}>
          <Icon name="info circle" color="yellow" />
          <span style={{ marginLeft: '8px' }}>Message length: {part.value} bytes</span>
        </div>
      ];
    default:
      return ["Unknown type"];
  }
}

function ProtobufPart(props) {
  const { part, originalData } = props;

  // 使用 useMemo 缓存解析结果，避免重复解析
  const [contents, subType] = useMemo(() => getProtobufPart(part, originalData), [part, originalData]);
  const stringType = useMemo(() => typeToString(part.type, subType), [part.type, subType]);
  const typeColor = useMemo(() => getTypeColor(part.type), [part.type]);

  return (
    <Table.Row>
      <Table.Cell style={{ wordBreak: 'break-word', verticalAlign: 'top' }}>
        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#666' }}>
          {formatByteRange(part.byteRange)}
        </div>
      </Table.Cell>
      <Table.Cell style={{ wordBreak: 'break-word', verticalAlign: 'top' }}>
        <div style={{ fontWeight: 'bold' }}>
          {formatFieldNumber(part.index)}
        </div>
      </Table.Cell>
      <Table.Cell style={{ wordBreak: 'break-word', verticalAlign: 'top' }}>
        <Label size="mini" color={typeColor}>
          {stringType}
        </Label>
      </Table.Cell>
      <Table.Cell style={{ wordBreak: 'break-word', verticalAlign: 'top', minWidth: '400px' }}>
        {contents}
      </Table.Cell>
    </Table.Row>
  );
}

export default React.memo(ProtobufPart);
